import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { X, Maximize2 } from "lucide-react";
import api from "../lib/api";
import { SectionHeader } from "../components/sections/About";

export default function GalleryPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/gallery");
        setItems(data || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="pt-24 pb-20" data-testid="gallery-page">
      <div className="max-w-7xl mx-auto px-6 sm:px-10">
        <SectionHeader number="08" overline="Gallery" title="Professional moments and events." />

        <div className="mt-14">
          {loading && <p className="text-fog-300">Loading gallery…</p>}

          {!loading && items.length === 0 && (
            <p className="text-fog-300">No images yet.</p>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((img, i) => (
            <motion.div
              key={img.id}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="card-tech overflow-hidden cursor-pointer group relative"
              onClick={() => setLightbox(img)}
              data-testid={`gallery-item-${i}`}
            >
              <div className="aspect-[4/3] bg-ink-900 overflow-hidden">
                <img
                  src={img.src}
                  alt={img.alt || img.caption || "Gallery image"}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              {(img.caption || img.alt) && (
                <div className="p-4">
                  <p className="text-sm text-fog-200 line-clamp-2">{img.caption || img.alt}</p>
                </div>
              )}
              <div className="absolute inset-0 bg-ink-950/0 group-hover:bg-ink-950/20 transition-colors flex items-center justify-center pointer-events-none">
                <Maximize2 className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-ink-950/90 backdrop-blur flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="gallery-lightbox"
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            className="absolute top-4 right-4 p-2 text-fog-300 hover:text-white"
            data-testid="gallery-lightbox-close"
          >
            <X className="w-6 h-6" />
          </button>
          <div className="max-w-5xl w-full max-h-[90vh] flex flex-col items-center">
            <img
              src={lightbox.src}
              alt={lightbox.alt || lightbox.caption || "Gallery image"}
              className="max-w-full max-h-[80vh] object-contain"
            />
            {(lightbox.caption || lightbox.alt) && (
              <p className="mt-4 text-fog-200 text-center">{lightbox.caption || lightbox.alt}</p>
            )}
          </div>
        </div>
      )}
    </div>
    </div>
  );
}
